import {
  type Comparator,
  comparisonFor,
} from "../../deliveryChargeRuleCommons/index.js";
import { calculateItemTotal } from "../../orderCommons/index.js";
import { getContainerTypes } from "../../productCommons/index.js";

export const calculateFulfillmentTypeContainerDeliveryCharge = ({
  orderItems,
  fulfillmentTypeContainerDeliveryCharges,
  deliveryChargeRules,
  calculatedContainerTypeDeliveryOffers,
  flatDeliveryCharge,
  deliveryChargeCap,
  freeDeliveryBasketSubtotal,
}: {
  orderItems?: {
    volume: number | null;
    packQuantity: number | null;
    displayUnit: string;
    containerType: string;
    price: number;
    quantity: number;
  }[];
  fulfillmentTypeContainerDeliveryCharges?: {
    volume: number;
    packQuantity: number;
    displayUnit: string;
    containerType: string;
    charge: number;
  }[];
  deliveryChargeRules?: {
    comparator: Comparator;
    value: number;
    deliveryCharge: number;
  }[];
  calculatedContainerTypeDeliveryOffers: {
    deliveryRule: { containerType: string };
    remainder: number;
    qualifiesForFreeDelivery: boolean;
  }[];
  flatDeliveryCharge?: number | null;
  deliveryChargeCap: number | null | undefined;
  freeDeliveryBasketSubtotal?: number | null | undefined;
}): {
  charge: number;
  isDeliveryChargeCapped: boolean;
} => {
  let changeByRule: number | undefined = undefined;

  for (const rule of deliveryChargeRules ?? []) {
    if (
      comparisonFor(
        rule.comparator,
        calculateItemTotal(
          orderItems?.map((orderItem) => ({
            quantity: orderItem.quantity,
            price: orderItem.price,
          })) ?? [],
        ),
        rule.value,
      )
    ) {
      changeByRule = rule.deliveryCharge;
    }
  }

  if (!!freeDeliveryBasketSubtotal) {
    if (
      comparisonFor(
        ">=",
        calculateItemTotal(
          orderItems?.map((orderItem) => ({
            quantity: orderItem.quantity,
            price: orderItem.price,
          })) ?? [],
        ),
        freeDeliveryBasketSubtotal,
      )
    ) {
      changeByRule = 0;
    }
  }

  if (Number.isInteger(changeByRule)) {
    return getDeliveryCharge({
      charge: changeByRule as number,
      deliveryChargeCap,
    });
  }

  if (!orderItems?.length || !fulfillmentTypeContainerDeliveryCharges?.length) {
    return getDeliveryCharge({
      charge: flatDeliveryCharge ?? 0,
      deliveryChargeCap,
    });
  }

  const calculatedCharge =
    orderItems.reduce((totalCharge, orderItem) => {
      const matchingItem = fulfillmentTypeContainerDeliveryCharges.find(
        (item) =>
          getContainerTypes(orderItem.containerType).includes(
            item.containerType,
          ) &&
          // @ts-expect-error
          (item.displayUnit === orderItem.displayUnit ?? "") &&
          item.packQuantity === orderItem.packQuantity &&
          item.volume === orderItem.volume,
      );

      const orderItemContainerTypes = getContainerTypes(
        orderItem.containerType,
      );

      const itemQualifiesForFreeDelivery =
        calculatedContainerTypeDeliveryOffers.some((offer) => {
          if (
            orderItemContainerTypes.includes(
              offer.deliveryRule.containerType,
            ) &&
            offer.qualifiesForFreeDelivery
          ) {
            return true;
          }
          return false;
        });

      if (itemQualifiesForFreeDelivery) {
        return totalCharge;
      }

      if (matchingItem) {
        return totalCharge + matchingItem.charge * orderItem.quantity;
      }

      return totalCharge;
    }, 0) + (flatDeliveryCharge ?? 0);

  return getDeliveryCharge({
    charge: calculatedCharge,
    deliveryChargeCap,
  });
};

const getDeliveryCharge = ({
  charge,
  deliveryChargeCap,
}: {
  charge: number;
  deliveryChargeCap: number | null | undefined;
}): {
  charge: number;
  isDeliveryChargeCapped: boolean;
} => {
  const isDeliveryChargeCapped =
    !!deliveryChargeCap && deliveryChargeCap < charge;
  return {
    charge: isDeliveryChargeCapped ? deliveryChargeCap : charge,
    isDeliveryChargeCapped,
  };
};
